import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  SettingSmallText,
  SettingsGroup,
  SettingsHr,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import Switch from 'approot/shared/switch/switch';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import {
  MultiView,
  MultiViewPage,
} from 'approot/shared/layout/layout-multi-view';
import StepInput from 'approot/shared/step-input/step-input';
import ValidationMessage from 'approot/shared/validation/validation-message';
import { FlexLeftAlign } from 'approot/shared/styles/flex.style';
import {
  addColumnsToTable,
  removeColumn,
  updateTableRows,
} from './table-settings.utils';
import {
  MAX_TABLE_COLUMNS,
  MAX_TABLE_ROWS,
  SettingsProps,
  TABLE_TYPENAME,
  Table,
} from './table.constants';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { Box } from 'approot/shared/design-system/box/box.style';
import type { SubActivityDetailsInput } from '../../../../gql/schema-types';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import {
  AnswerInput,
  getSuggestedAnswerPayload,
  useBatchSuggestedAnswersContext,
} from '../shared/batch-suggested-answers.context';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import {
  getSuggestedAnswersInput,
  getTableAnswerMap,
  updateAnswerMapAfterColumnDelete,
  updateAnswerMapAfterRowDelete,
} from '../shared/logic/table-answer-map';
import { SUGGESTED_ANSWER_EXAMPLE } from 'global/constants';

const NUMBER_OF_COLUMNS_FIELD = 'numberOfColumns';
const NUMBER_OF_ROWS_FIELD = 'numberOfRows';

type FormData = {
  [NUMBER_OF_ROWS_FIELD]: string;
  [NUMBER_OF_COLUMNS_FIELD]: string;
};

const TableSettings = ({
  sectionId,
  id,
  type,
  sort,
  properties,
  subActivityProperties,
  suggestedAnswerProperties,
  onCloseChildDrawer,
}: SettingsProps) => {
  const { closeDrawer } = useActivityBuilderContext();

  const answersMap = useMemo(
    () => getTableAnswerMap(suggestedAnswerProperties?.suggestedAnswers || []),
    [suggestedAnswerProperties?.suggestedAnswers]
  );

  const {
    formState: { errors },
    watch,
    setValue,
    register,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [NUMBER_OF_ROWS_FIELD]: `${properties.table.rows.length}`,
      [NUMBER_OF_COLUMNS_FIELD]: `${properties.table.rows[0].cells.length}`,
    },
  });
  const [numberOfRows, numberOfColumns] = watch([
    NUMBER_OF_ROWS_FIELD,
    NUMBER_OF_COLUMNS_FIELD,
  ]);

  const { updateComponentProperties } = useBatchComponentUpdateContext();
  const { updateSuggestedAnswerApollo } = useBatchSuggestedAnswersContext();

  const handleTableChange = useCallback(
    (props: {
      table: Table;
      numberOfRows: number;
      numberOfColumns: number;
      subActivityDetails?: SubActivityDetailsInput;
      suggestedAnswerInput?: AnswerInput;
    }) => {
      const {
        table,
        numberOfRows,
        numberOfColumns,
        subActivityDetails,
        suggestedAnswerInput,
      } = props;

      if (suggestedAnswerInput) {
        updateSuggestedAnswerApollo({
          componentId: id,
          input: suggestedAnswerInput,
          typename: TABLE_TYPENAME,
        });
      }

      const tableProperties = {
        ...table,
        columnWidths:
          table.columnWidths.length === numberOfColumns
            ? table.columnWidths
            : table.columnWidths.length > numberOfColumns
              ? removeColumn(props.table.columnWidths, numberOfColumns)
              : addColumnsToTable(props.table.columnWidths, numberOfColumns),
        rows: updateTableRows(table.rows, numberOfRows, numberOfColumns),
      };

      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          subActivityDetails: subActivityDetails
            ? subActivityDetails
            : undefined,
          ...(suggestedAnswerInput
            ? getSuggestedAnswerPayload(suggestedAnswerInput)
            : undefined),
          componentData: {
            type,
            sort,
            properties: {
              table: tableProperties,
            },
          },
        },
        properties: {
          ...properties,
          table: {
            ...tableProperties,
          },
        },
        typename: TABLE_TYPENAME,
      });
    },
    [
      id,
      sectionId,
      type,
      sort,
      properties,
      updateComponentProperties,
      updateSuggestedAnswerApollo,
    ]
  );

  const handleRowIncrement = useCallback(
    (props: {
      table: Table;
      numberOfRows: string;
      numberOfColumns: string;
    }) => {
      const currentRows = Number(props.numberOfRows);
      const value = currentRows + 1;
      setValue(NUMBER_OF_ROWS_FIELD, `${value}`, { shouldValidate: true });
      if (value <= MAX_TABLE_ROWS) {
        handleTableChange({
          ...props,
          numberOfColumns: Number(props.numberOfColumns),
          numberOfRows: value,
        });
      }
    },
    [setValue, handleTableChange]
  );

  const handleRowDecrement = useCallback(
    (props: {
      table: Table;
      numberOfRows: string;
      numberOfColumns: string;
    }) => {
      const currentRows = Number(props.numberOfRows);
      const value = currentRows - 1;
      setValue(NUMBER_OF_ROWS_FIELD, `${value}`, { shouldValidate: true });
      if (value >= 1) {
        const updatedAnswers = updateAnswerMapAfterRowDelete(answersMap, value);

        handleTableChange({
          ...props,
          numberOfColumns: Number(props.numberOfColumns),
          numberOfRows: value,
          suggestedAnswerInput: updatedAnswers
            ? getSuggestedAnswersInput(
                updatedAnswers,
                suggestedAnswerProperties
              )
            : undefined,
        });
      }
    },
    [answersMap, suggestedAnswerProperties, setValue, handleTableChange]
  );

  const handleColumnIncrement = useCallback(
    (props: {
      table: Table;
      numberOfRows: string;
      numberOfColumns: string;
    }) => {
      const current = Number(props.numberOfColumns);
      const value = current + 1;
      setValue(NUMBER_OF_COLUMNS_FIELD, `${value}`, { shouldValidate: true });
      if (value <= MAX_TABLE_COLUMNS) {
        handleTableChange({
          ...props,
          numberOfRows: Number(props.numberOfRows),
          numberOfColumns: value,
        });
      }
    },
    [handleTableChange, setValue]
  );

  const handleColumnDecrement = useCallback(
    (props: {
      table: Table;
      numberOfRows: string;
      numberOfColumns: string;
    }) => {
      const current = Number(props.numberOfColumns);
      const value = current - 1;
      setValue(NUMBER_OF_COLUMNS_FIELD, `${value}`, { shouldValidate: true });
      if (value >= 1) {
        const updatedAnswers = updateAnswerMapAfterColumnDelete(
          answersMap,
          value
        );
        handleTableChange({
          ...props,
          numberOfRows: Number(props.numberOfRows),
          numberOfColumns: value,
          suggestedAnswerInput: updatedAnswers
            ? getSuggestedAnswersInput(
                updatedAnswers,
                suggestedAnswerProperties
              )
            : undefined,
        });
      }
    },
    [answersMap, suggestedAnswerProperties, handleTableChange, setValue]
  );

  useEffect(() => {
    setValue(
      NUMBER_OF_COLUMNS_FIELD,
      `${properties.table.rows[0].cells.length}`,
      { shouldValidate: true }
    );
    setValue(NUMBER_OF_ROWS_FIELD, `${properties.table.rows.length}`, {
      shouldValidate: true,
    });
  }, [properties, setValue]);

  return (
    <MultiView>
      {multiviewProps => (
        <MultiViewPage
          index={0}
          onClose={closeDrawer}
          {...multiviewProps}
          renderItem={() => (
            <form noValidate onSubmit={e => e.preventDefault()}>
              <ActivityBuilderDrawerWrapper>
                <ActivityBuilderDrawerHeader
                  title="Table"
                  isBack={!!onCloseChildDrawer}
                  onClose={
                    onCloseChildDrawer ? onCloseChildDrawer : closeDrawer
                  }
                />
                <ActivityBuilderDrawerContent>
                  <SettingsGroup>
                    <SettingsLabel>Table Purpose</SettingsLabel>
                    <Box $marginBottom={8}>
                      <CheckableSingle
                        variant="pill"
                        name="tablePurpose"
                        options={[
                          {
                            label: 'Response',
                            value: 'response',
                          },
                          {
                            label: 'Information Only',
                            value: 'readonly',
                          },
                        ]}
                        selected={
                          properties.table.isReadOnly ? 'readonly' : 'response'
                        }
                        onSelect={value =>
                          handleTableChange({
                            table: {
                              ...properties.table,
                              isReadOnly: value === 'readonly',
                            },
                            numberOfColumns: Number(numberOfColumns),
                            numberOfRows: Number(numberOfRows),
                            subActivityDetails: subActivityProperties
                              ? {
                                  areInstructionsVisible: value === 'response',
                                  subActivitySort:
                                    subActivityProperties.subActivitySort,
                                  instructions:
                                    subActivityProperties.instructions,
                                }
                              : undefined,
                            suggestedAnswerInput: {
                              __typename: 'ComponentSuggestedAnswersProperties',
                              suggestedAnswerType: SUGGESTED_ANSWER_EXAMPLE,
                              suggestedAnswers: null,
                              suggestedAnswersScore: 1,
                              markingGuides:
                                suggestedAnswerProperties?.markingGuides ||
                                null,
                            },
                          })
                        }
                      />
                    </Box>

                    {properties.table.isReadOnly ? (
                      <SettingSmallText marginBottom={0}>
                        Show information in a table without the ability for
                        students and teachers to respond in empty cells.
                      </SettingSmallText>
                    ) : (
                      <SettingSmallText marginBottom={0}>
                        Show instruction and enable users to type their answers
                        into any empty table cells.
                      </SettingSmallText>
                    )}
                  </SettingsGroup>

                  <SettingsHr />
                  <SettingsGroup>
                    <SettingsLabel>Header Column</SettingsLabel>
                    <Switch
                      ariaLabel={`${
                        properties.table.showColumnHeaders ? 'Hide' : 'Show'
                      } header column`}
                      state={properties.table.showColumnHeaders}
                      onClick={() => {
                        handleTableChange({
                          table: {
                            ...properties.table,
                            showColumnHeaders:
                              !properties.table.showColumnHeaders,
                          },
                          numberOfColumns: Number(numberOfColumns),
                          numberOfRows: Number(numberOfRows),
                        });
                      }}
                    />
                  </SettingsGroup>

                  <SettingsGroup>
                    <SettingsLabel htmlFor={NUMBER_OF_COLUMNS_FIELD}>
                      Number of Columns
                    </SettingsLabel>
                    <FlexLeftAlign>
                      <StepInput
                        data-selector="no-of-columns"
                        id={NUMBER_OF_COLUMNS_FIELD}
                        disabled
                        {...register(NUMBER_OF_COLUMNS_FIELD, {
                          max: {
                            value: MAX_TABLE_COLUMNS,
                            message: `A table must have no more than ${MAX_TABLE_COLUMNS} columns`,
                          },
                          min: {
                            value: 1,
                            message: 'A table must have at least 1 column',
                          },
                        })}
                        onIncrementClick={() =>
                          handleColumnIncrement({
                            table: properties.table,
                            numberOfRows,
                            numberOfColumns,
                          })
                        }
                        onDecrementClick={() =>
                          handleColumnDecrement({
                            table: properties.table,
                            numberOfRows,
                            numberOfColumns,
                          })
                        }
                        isDecrementDisabled={Number(numberOfColumns) <= 1}
                        isIncrementDisabled={
                          Number(numberOfColumns) >= MAX_TABLE_COLUMNS
                        }
                        aria-invalid={
                          !!errors[NUMBER_OF_COLUMNS_FIELD]?.message
                        }
                        aria-describedby={`${NUMBER_OF_COLUMNS_FIELD}-error`}
                      />
                    </FlexLeftAlign>
                    <ValidationMessage
                      id={`${NUMBER_OF_COLUMNS_FIELD}-error`}
                      field={NUMBER_OF_COLUMNS_FIELD}
                      errors={errors}
                    />
                  </SettingsGroup>

                  <SettingsHr />
                  <SettingsGroup>
                    <SettingsLabel>Header Row</SettingsLabel>
                    <Switch
                      ariaLabel={`${
                        properties.table.showRowHeaders ? 'Hide' : 'Show'
                      } header row`}
                      state={properties.table.showRowHeaders}
                      onClick={() => {
                        handleTableChange({
                          table: {
                            ...properties.table,
                            showRowHeaders: !properties.table.showRowHeaders,
                          },
                          numberOfColumns: Number(numberOfColumns),
                          numberOfRows: Number(numberOfRows),
                        });
                      }}
                    />
                  </SettingsGroup>

                  <SettingsGroup>
                    <SettingsLabel htmlFor={NUMBER_OF_ROWS_FIELD}>
                      Number of Rows
                    </SettingsLabel>
                    <FlexLeftAlign>
                      <StepInput
                        data-selector="no-of-rows"
                        id={NUMBER_OF_ROWS_FIELD}
                        disabled
                        defaultValue={numberOfRows}
                        {...register(NUMBER_OF_ROWS_FIELD, {
                          max: {
                            value: MAX_TABLE_ROWS,
                            message: `A table must have no more than ${MAX_TABLE_ROWS} rows`,
                          },
                          min: {
                            value: 1,
                            message: 'A table must have at least 1 row',
                          },
                        })}
                        onIncrementClick={() =>
                          handleRowIncrement({
                            table: properties.table,
                            numberOfColumns,
                            numberOfRows,
                          })
                        }
                        onDecrementClick={() =>
                          handleRowDecrement({
                            table: properties.table,
                            numberOfColumns,
                            numberOfRows,
                          })
                        }
                        isDecrementDisabled={Number(numberOfRows) <= 1}
                        isIncrementDisabled={
                          Number(numberOfRows) >= MAX_TABLE_ROWS
                        }
                        aria-invalid={!!errors[NUMBER_OF_ROWS_FIELD]?.message}
                        aria-describedby={`${NUMBER_OF_ROWS_FIELD}-error`}
                      />
                    </FlexLeftAlign>
                    <ValidationMessage
                      id={`${NUMBER_OF_ROWS_FIELD}-error`}
                      field={NUMBER_OF_ROWS_FIELD}
                      errors={errors}
                    />
                  </SettingsGroup>
                  <SettingsHr $marginBottom={24} />
                  <SettingsGroup>
                    <SecondaryButton
                      type="button"
                      disabled={properties.table.columnWidths.length === 1}
                      onClick={() => {
                        handleTableChange({
                          table: {
                            ...properties.table,
                            columnWidths: properties.table.columnWidths.map(c =>
                              Math.floor(
                                1048 / properties.table.columnWidths.length
                              )
                            ),
                          },
                          numberOfColumns: Number(numberOfColumns),
                          numberOfRows: Number(numberOfRows),
                        });
                      }}
                      data-selector="btn-distribute-columns"
                    >
                      Distribute Columns Evenly
                    </SecondaryButton>
                  </SettingsGroup>
                </ActivityBuilderDrawerContent>
              </ActivityBuilderDrawerWrapper>
            </form>
          )}
        />
      )}
    </MultiView>
  );
};

export default TableSettings;
